/*
 * /auth/reports (page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Checkbox,
	Container,
	DatePickerFormField,
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Form,
	Label,
	PageHeader,
	PageWithEffect,
	toast,
} from "@emr/react-ui"
import type { ClientMasterFilters, Producer, ReportJob } from "@emr/db/client"
import { CLIENT_STATUSES } from "@emr/db/client"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ChevronDown, Download, FileSpreadsheet, Loader2, RefreshCw, Stethoscope, UserMinus, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { z } from "zod"
import { format } from "date-fns"
import { trpc } from "../../trpc"
import { adminPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/reports")({
	component: ReportsPage,
	loader: adminPageLoader,
})

/* ─── Constants ──────────────────────────────────────────────────────────── */

const STATUS_LABELS: Record<string, string> = {
	review_required: "Review Required",
	education_required: "Education Required",
	education_scheduled: "Education Scheduled",
	prescription_required: "Prescription Required",
	prescription_scheduled: "Prescription Scheduled",
	active: "Active",
	sla_required: "SLA Required",
	sla_scheduled: "SLA Scheduled",
	renewal_required: "Renewal Required",
	not_active: "Not Active",
}

const dateRangeSchema = z.object({
	from: z.string().optional(),
	to: z.string().optional(),
})
const filterFormSchema = z.object({
	vacApprovalEnd: dateRangeSchema.optional(),
	prescriptionExpiry: dateRangeSchema.optional(),
})
type FilterFormValues = z.infer<typeof filterFormSchema>

/* ─── Multi-select dropdown ─────────────────────────────────────────────── */

function MultiSelect({
	label,
	options,
	selected,
	onChange,
}: {
	label: string
	options: { label: string; value: string }[]
	selected: string[]
	onChange: (v: string[]) => void
}) {
	function toggle(value: string) {
		onChange(
			selected.includes(value) ? selected.filter((s) => s !== value) : [...selected, value],
		)
	}

	return (
		<div className="flex flex-col gap-1.5">
			<Label className="text-sm font-medium">{label}</Label>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" className="justify-between font-normal h-9 text-sm">
						<span className="text-muted-foreground truncate">
							{selected.length === 0
								? "All (leave empty)"
								: `${selected.length} selected`}
						</span>
						<ChevronDown className="w-3.5 h-3.5 ml-2 shrink-0 opacity-50" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent className="w-64 max-h-72 overflow-y-auto">
					<DropdownMenuLabel>{label}</DropdownMenuLabel>
					<DropdownMenuSeparator />
					{options.map((opt) => (
						<DropdownMenuCheckboxItem
							key={opt.value}
							checked={selected.includes(opt.value)}
							onCheckedChange={() => toggle(opt.value)}
						>
							{opt.label}
						</DropdownMenuCheckboxItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
			{selected.length > 0 && (
				<div className="flex flex-wrap gap-1 mt-1">
					{selected.map((v) => {
						const opt = options.find((o) => o.value === v)
						return (
							<Badge key={v} variant="secondary" className="text-xs gap-1">
								{opt?.label ?? v}
								<button
									type="button"
									onClick={() => toggle(v)}
									className="hover:opacity-70"
								>
									<X className="w-3 h-3" />
								</button>
							</Badge>
						)
					})}
				</div>
			)}
		</div>
	)
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

function ReportsPage() {
	const [activePrescriptionOnly, setActivePrescriptionOnly] = useState(false)
	const [selectedProducers, setSelectedProducers] = useState<string[]>([])
	const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])

	const { data: producers } = trpc.producer.getAllByFilter.useQuery({ where: { active: true } })
	const producerOptions = (producers ?? []).map((p) => ({ label: p.name, value: p.id }))
	const statusOptions = CLIENT_STATUSES.map((s) => ({ label: STATUS_LABELS[s] ?? s, value: s }))

	const { data: jobs, isLoading, refetch } = trpc.reportJob.getAllByFilter.useQuery(
		{ where: { active: true } },
		{ select: (data) => [...(data ?? [])].sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()) },
	)

	const hasPending = (jobs ?? []).some(
		(j) => j.status === "pending" || j.status === "processing",
	)

	/* Auto-refresh every 30s when a job is pending/processing */
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const [countdown, setCountdown] = useState(30)

	useEffect(() => {
		if (!hasPending) {
			if (intervalRef.current) clearInterval(intervalRef.current)
			intervalRef.current = null
			setCountdown(30)
			return
		}

		let secs = 30
		setCountdown(secs)

		intervalRef.current = setInterval(() => {
			secs -= 1
			setCountdown(secs)
			if (secs <= 0) {
				refetch()
				secs = 30
				setCountdown(secs)
			}
		}, 1000)

		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current)
		}
	}, [hasPending, refetch])

	const queue = trpc.reportJob.queue.useMutation({
		onSuccess: () => {
			toast.success("Report queued — check the history below")
			refetch()
		},
		onError: (e) => toast.error(e.message),
	})

	async function handleRun(values: FilterFormValues) {
		const filters: ClientMasterFilters = {
			activePrescriptionOnly,
			producerIds: selectedProducers.length ? selectedProducers : undefined,
			clientStatuses: selectedStatuses.length ? selectedStatuses : undefined,
			vacApprovalEndFrom: values.vacApprovalEnd?.from || undefined,
			vacApprovalEndTo: values.vacApprovalEnd?.to || undefined,
			prescriptionExpiryFrom: values.prescriptionExpiry?.from || undefined,
			prescriptionExpiryTo: values.prescriptionExpiry?.to || undefined,
		}
		queue.mutate({ reportType: "client_master", filters })
	}

	return (
		<PageWithEffect effect="aurora" centerVertical={false}>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader title="Reports" />

				{/* Filter card */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<FileSpreadsheet className="w-5 h-5" />
							Client Master Report
						</CardTitle>
						<CardDescription>
							Generates an Excel file with client details, prescriptions, VAC coverage, and diagnoses.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Form<FilterFormValues>
							schema={filterFormSchema}
							defaultValues={{}}
							onSubmit={async (v) => handleRun(v)}
							className="flex flex-col gap-5"
						>
							{/* Active prescription toggle */}
							<div className="flex items-center gap-3">
								<Checkbox
									id="activePrescriptionOnly"
									checked={activePrescriptionOnly}
									onCheckedChange={(c) => setActivePrescriptionOnly(!!c)}
								/>
								<Label htmlFor="activePrescriptionOnly" className="cursor-pointer">
									Active Prescription Only
								</Label>
							</div>

							{/* Multiselects */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<MultiSelect
									label="Licensed Producer (empty = all)"
									options={producerOptions}
									selected={selectedProducers}
									onChange={setSelectedProducers}
								/>
								<MultiSelect
									label="Client Status (empty = all)"
									options={statusOptions}
									selected={selectedStatuses}
									onChange={setSelectedStatuses}
								/>
							</div>

							{/* Date filters */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="flex flex-col gap-2">
									<Label className="text-sm font-medium">VAC Approval End — Range</Label>
									<div className="grid grid-cols-2 gap-2">
										<DatePickerFormField name="vacApprovalEnd.from" label="From" />
										<DatePickerFormField name="vacApprovalEnd.to" label="To" />
									</div>
								</div>
								<div className="flex flex-col gap-2">
									<Label className="text-sm font-medium">Prescription Expiry — Range</Label>
									<div className="grid grid-cols-2 gap-2">
										<DatePickerFormField name="prescriptionExpiry.from" label="From" />
										<DatePickerFormField name="prescriptionExpiry.to" label="To" />
									</div>
								</div>
							</div>

							<div>
								<Button type="submit" disabled={queue.isPending}>
									{queue.isPending ? (
										<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Queuing…</>
									) : (
										<><FileSpreadsheet className="w-4 h-4 mr-2" />Run Report</>
									)}
								</Button>
							</div>
						</Form>
					</CardContent>
				</Card>

				{/* Deactivated clients */}
				<DeactivatedClientsCard onQueued={refetch} />

				{/* LP Billing */}
				<LpBillingReportCard producers={producers ?? []} onQueued={refetch} />

				{/* Massage Billing */}
				<MassageBillingLinkCard />

				{/* History card */}
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle>Report History</CardTitle>
								{hasPending && (
									<CardDescription className="flex items-center gap-1.5 mt-1">
										<RefreshCw className="w-3.5 h-3.5 animate-spin" />
										Auto-refreshing in {countdown}s
									</CardDescription>
								)}
							</div>
							<Button variant="ghost" size="sm" onClick={() => refetch()}>
								<RefreshCw className="w-3.5 h-3.5 mr-1" />
								Refresh
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						{isLoading ? (
							<p className="text-sm text-muted-foreground">Loading…</p>
						) : !jobs?.length ? (
							<p className="text-sm text-muted-foreground">
								No reports yet. Run your first report above.
							</p>
						) : (
							<div className="rounded-lg border divide-y">
								{jobs.map((job) => (
									<ReportJobRow key={job.id} job={job} />
								))}
							</div>
						)}
					</CardContent>
				</Card>
			</Container>
		</PageWithEffect>
	)
}

/* ─── Deactivated clients report card ───────────────────────────────────── */

const deactivatedFilterSchema = z.object({
	deactivatedFrom: z.string().optional(),
	deactivatedTo: z.string().optional(),
})
type DeactivatedFilterValues = z.infer<typeof deactivatedFilterSchema>

function DeactivatedClientsCard({ onQueued }: { onQueued: () => void }) {
	const queue = trpc.reportJob.queue.useMutation({
		onSuccess: () => {
			toast.success("Report queued — check the history below")
			onQueued()
		},
		onError: (e) => toast.error(e.message),
	})

	function handleRun(values: DeactivatedFilterValues) {
		queue.mutate({
			reportType: "deactivated_clients",
			filters: {
				deactivatedFrom: values.deactivatedFrom || undefined,
				deactivatedTo: values.deactivatedTo || undefined,
			},
		})
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<UserMinus className="w-5 h-5" />
					Deactivated Clients Report
				</CardTitle>
				<CardDescription>
					Generates an Excel file with Client #, Name, DOB, K-Number, Address, Email, Phone, Date Inactive, and Deactivation Reason.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<Form<DeactivatedFilterValues>
					schema={deactivatedFilterSchema}
					defaultValues={{}}
					onSubmit={async (v) => handleRun(v)}
					className="flex flex-col gap-5"
				>
					<div className="flex flex-col gap-2">
						<Label className="text-sm font-medium">Deactivation Date — Range</Label>
						<div className="grid grid-cols-2 gap-2 max-w-sm">
							<DatePickerFormField name="deactivatedFrom" label="From" />
							<DatePickerFormField name="deactivatedTo" label="To" />
						</div>
						<p className="text-xs text-muted-foreground">Leave empty to include all deactivated clients.</p>
					</div>

					<div>
						<Button type="submit" disabled={queue.isPending}>
							{queue.isPending ? (
								<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Queuing…</>
							) : (
								<><UserMinus className="w-4 h-4 mr-2" />Run Report</>
							)}
						</Button>
					</div>
				</Form>
			</CardContent>
		</Card>
	)
}

/* ─── LP Billing report card ─────────────────────────────────────────────── */

function LpBillingReportCard({ producers, onQueued }: { producers: Producer[]; onQueued: () => void }) {
	const [selectedProducerId, setSelectedProducerId] = useState<string>("")

	const queue = trpc.reportJob.queue.useMutation({
		onSuccess: () => {
			toast.success("LP Billing report queued — check the history below")
			onQueued()
		},
		onError: (e) => toast.error(e.message),
	})

	function handleRun() {
		if (!selectedProducerId) return
		queue.mutate({ reportType: "lp_billing", filters: { producerId: selectedProducerId } })
	}

	const producerOptions = producers.map((p) => ({ label: p.name, value: p.id }))

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Stethoscope className="w-5 h-5" />
					LP Billing Report
				</CardTitle>
				<CardDescription>
					Generates an Excel file with active clients for a specific Licensed Producer including Valor Client #, K Number, DOB, Grams Assigned, RX Expiry Date, and LP Client ID.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<div className="flex flex-col gap-5">
					<div className="flex flex-col gap-1.5">
						<Label className="text-sm font-medium">Licensed Producer</Label>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" className="justify-between font-normal h-9 text-sm max-w-xs">
									<span className={selectedProducerId ? "" : "text-muted-foreground"}>
										{selectedProducerId
											? (producerOptions.find((p) => p.value === selectedProducerId)?.label ?? "Select LP")
											: "Select a Licensed Producer"}
									</span>
									<ChevronDown className="w-3.5 h-3.5 ml-2 shrink-0 opacity-50" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent className="w-64 max-h-72 overflow-y-auto">
								<DropdownMenuLabel>Licensed Producer</DropdownMenuLabel>
								<DropdownMenuSeparator />
								{producerOptions.map((opt) => (
									<DropdownMenuCheckboxItem
										key={opt.value}
										checked={selectedProducerId === opt.value}
										onCheckedChange={() => setSelectedProducerId(opt.value)}
									>
										{opt.label}
									</DropdownMenuCheckboxItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					</div>

					<div>
						<Button onClick={handleRun} disabled={!selectedProducerId || queue.isPending}>
							{queue.isPending ? (
								<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Queuing…</>
							) : (
								<><FileSpreadsheet className="w-4 h-4 mr-2" />Run Report</>
							)}
						</Button>
					</div>
				</div>
			</CardContent>
		</Card>
	)
}

/* ─── Massage billing link card ──────────────────────────────────────────── */

function MassageBillingLinkCard() {
	return (
		<Card>
			<CardHeader>
				<div className="flex items-center gap-3">
					<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
						<Stethoscope className="w-5 h-5 text-primary" />
					</div>
					<div>
						<CardTitle>Massage Billing Report</CardTitle>
						<CardDescription>
							Generate a VAC billing report for completed massage therapy sessions.
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				<Link to="/auth/massage-report">
					<Button size="sm">
						<FileSpreadsheet className="w-4 h-4 mr-2" />
						Open Report
					</Button>
				</Link>
			</CardContent>
		</Card>
	)
}

/* ─── Job row ─────────────────────────────────────────────────────────────── */

const REPORT_LABELS: Record<string, string> = {
	client_master: "Client Master Report",
	deactivated_clients: "Deactivated Clients Report",
	lp_billing: "LP Billing Report",
	massage_billing: "Massage Billing Report",
}

function ReportJobRow({ job }: { job: ReportJob }) {
	const [downloading, setDownloading] = useState(false)
	const downloadQuery = trpc.reportJob.download.useQuery(
		{ id: job.id },
		{ enabled: false },
	)

	const filters: ClientMasterFilters = (() => {
		try { return job.filters ? JSON.parse(job.filters) : {} } catch { return {} }
	})()

	async function handleDownload() {
		setDownloading(true)
		try {
			const result = await downloadQuery.refetch()
			if (result.data?.url) {
				const a = document.createElement("a")
				a.href = result.data.url
				a.download = `client-master-${format(new Date(job.createdAt!), "yyyy-MM-dd-HHmm")}.xlsx`
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
			}
		} catch (e: any) {
			toast.error(e.message ?? "Download failed")
		} finally {
			setDownloading(false)
		}
	}

	return (
		<div className="flex items-center gap-3 px-4 py-3">
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="text-sm font-medium">
						{REPORT_LABELS[job.reportType] ?? job.reportType}
					</span>
					<StatusBadge status={job.status} />
					{job.rowCount != null && (
						<span className="text-xs text-muted-foreground">{job.rowCount.toLocaleString()} rows</span>
					)}
				</div>
				<div className="flex items-center gap-3 mt-0.5 flex-wrap">
					<span className="text-xs text-muted-foreground">
						{job.createdAt
							? format(new Date(job.createdAt), "MMM d, yyyy 'at' h:mm a")
							: "—"}
					</span>
					{filters.activePrescriptionOnly && (
						<span className="text-xs text-muted-foreground">· Active RX only</span>
					)}
					{(filters.clientStatuses?.length ?? 0) > 0 && (
						<span className="text-xs text-muted-foreground">
							· {filters.clientStatuses!.length} status filter{filters.clientStatuses!.length > 1 ? "s" : ""}
						</span>
					)}
					{job.status === "error" && job.errorMessage && (
						<span className="text-xs text-destructive">· {job.errorMessage}</span>
					)}
				</div>
			</div>
			<div className="shrink-0">
				{job.status === "complete" ? (
					<Button size="sm" variant="outline" onClick={handleDownload} disabled={downloading}>
						{downloading ? (
							<Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
						) : (
							<Download className="w-3.5 h-3.5 mr-1" />
						)}
						Download
					</Button>
				) : (job.status === "pending" || job.status === "processing") ? (
					<Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
				) : null}
			</div>
		</div>
	)
}

/* ─── Status badge ───────────────────────────────────────────────────────── */

function StatusBadge({ status }: { status: string }) {
	const map: Record<string, { label: string; cls: string }> = {
		pending:    { label: "Pending",    cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
		processing: { label: "Processing", cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
		complete:   { label: "Complete",   cls: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" },
		error:      { label: "Error",      cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
	}
	const v = map[status] ?? { label: status, cls: "bg-muted text-muted-foreground" }
	return (
		<span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${v.cls}`}>
			{v.label}
		</span>
	)
}
